import { NormalButton, SquareButton } from "@/frontend/shared/ui/Button";
import styles from "./Pagination.module.scss";
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon } from "@/frontend/shared/assets/icons";
import { Dispatch, SetStateAction, useState } from "react";
import { Typography } from "@/frontend/shared/ui/Typography";
import clsx from "clsx";
import { DropshadowMenu } from "../DropshadowMenu";

type TPaginationProps = {
  setPage: Dispatch<SetStateAction<number>>;
  companiesLimit: number;
  companiesTotal?: number;
  currentPage: number;
  className: string;
  isLoading: boolean;
};

export function Pagination({
  setPage,
  companiesLimit,
  companiesTotal = 0,
  currentPage,
  className,
  isLoading
}: TPaginationProps) {
  const hasNext = companiesTotal > currentPage * companiesLimit;
  const hasPrev = currentPage > 1;
  const totalPages = Math.ceil(companiesTotal / companiesLimit);
  const startIndex = (currentPage - 1) * companiesLimit + 1;
  const endIndex = Math.min(startIndex + companiesLimit - 1, companiesTotal);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const companiesPool = companiesTotal
    ? companiesTotal <= companiesLimit * 5
      ? companiesTotal.toString()
      : `${companiesLimit * 5}+`
    : "";

  function onClickNextPage() {
    if (hasNext) {
      setPage((prev) => prev + 1);
    }
  }

  function onClickPrevPage() {
    if (hasPrev) {
      setPage((prev) => prev - 1);
    }
  }

  const canGoPrev = !(isLoading || !hasPrev || currentPage == 1);
  const canGoNext = !(isLoading || !hasNext || currentPage == totalPages);

  return (
    <div className={clsx(styles.container, className)}>
      <Typography variant="label-4" color="greyscale600">
        {`${startIndex}-${endIndex} of ${companiesPool}`}
      </Typography>
      <div className={styles.subContainer}>
        {canGoPrev && (
          <SquareButton
            variant="secondary"
            size="small"
            className={styles.squareButton}
            onClick={onClickPrevPage}
            isDisabled={!canGoPrev}
          >
            <CaretLeftIcon />
          </SquareButton>
        )}
        <div style={{ position: "relative" }}>
          <NormalButton
            variant="secondary"
            size="small"
            className={styles.changePageButton}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsDropdownOpen((prev) => !prev);
            }}
          >
            {currentPage}
            <CaretDownIcon />
          </NormalButton>
          <DropshadowMenu
            className={styles.dropshadowMenu}
            options={[
              currentPage - 3 > 0 && currentPage - 3,
              currentPage - 2 > 0 && currentPage - 2,
              currentPage - 1 > 0 && currentPage - 1,
              currentPage,
              currentPage + 1 <= totalPages && currentPage + 1,
              currentPage + 2 <= totalPages && currentPage + 2,
              currentPage + 3 <= totalPages && currentPage + 3
            ].map((page) => {
              if (!page) return null;
              return (
                <NormalButton
                  variant="tertiary"
                  size="small"
                  className={styles.dropshadowOption}
                  key={page}
                  onClick={() => setPage(page)}
                >
                  {page}
                </NormalButton>
              );
            })}
            isOpen={isDropdownOpen}
            bottom="30px"
            onClose={() => setIsDropdownOpen(false)}
            minWidth="100%"
          />
        </div>
        {canGoNext && (
          <SquareButton
            variant="secondary"
            size="small"
            className={styles.squareButton}
            onClick={onClickNextPage}
            isDisabled={!canGoNext}
          >
            <CaretRightIcon />
          </SquareButton>
        )}
      </div>
    </div>
  );
}
